import { TraitCode } from '@capital-markets-gateway/api-client-identity';
import { useCheckAccountTraits } from '@cmg/auth';
import type { ClientDataGridProps, ColGroupDef, DataGridContextType } from '@cmg/data-grid';
import { ClientDataGrid, useDataGridContext } from '@cmg/data-grid';
import { styled } from '@cmg/design-system';
import size from 'lodash/size';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useStoredGridState } from '../../../../common/hooks/useStoredGridState';
import { selectUserSettingUseCustomSectors } from '../../../shared/ducks';
import { useSideBarDef } from '../../hooks/useSideBarDef';
import {
  CALENDAR_GRID_CELL_CLASS_NAMES,
  CalendarGridContext,
  CalendarGridRowData,
  getDefaultColDefOverride,
  getMainMenuItems,
  getRowId,
  groupRowRendererParams,
} from './CalendarGrid.model';
import { useCalendarGridDataContext } from './hooks/useCalendarGridDataContext';

type OwnProps = {
  filterLayout?: ColGroupDef[];
  storageKey: string;
  reverseNullSort?: boolean;
  dataGridContextRef: MutableRefObject<DataGridContextType | undefined>;
  onActiveFilterCountChange?: (activeFilterCount: number) => void;
};

export type CalendarGridProps = Pick<
  ClientDataGridProps<CalendarGridRowData>,
  'columnDefs' | 'rowData' | 'initialState' | 'onGridReady'
> &
  OwnProps;

const StyledClientDataGrid = styled(ClientDataGrid<CalendarGridRowData, CalendarGridContext>)(
  ({ theme }) => ({
    [`.${CALENDAR_GRID_CELL_CLASS_NAMES.Revised}::before`]: {
      ...theme.typography.highlight1,
      content: '"🅁 "',
    },
  })
);

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  columnDefs,
  rowData,
  storageKey,
  reverseNullSort = false,
  filterLayout,
  initialState = {},
  onGridReady,
  onActiveFilterCountChange,
  dataGridContextRef,
}) => {
  const hasExportTrait = useCheckAccountTraits([TraitCode.DL_EXCEL_DOWNLOAD]);

  const [initialStateApplied, setInitialStateApplied] = useState<boolean>(false);
  const dataGridContext = useDataGridContext();
  const { applyGridState, getState, isGridReady } = dataGridContext;

  const { gridState, handleStateUpdated } = useStoredGridState<
    CalendarGridRowData,
    CalendarGridContext
  >({
    initialState,
    storageKey,
  });

  const withCustomSectors = useSelector(selectUserSettingUseCustomSectors);
  const gridContext = useCalendarGridDataContext({ withCustomSectors });
  const sideBarDef = useSideBarDef({ filterLayout });

  useEffect(() => {
    dataGridContextRef.current = dataGridContext;
  }, [dataGridContext, dataGridContextRef]);
  useEffect(() => {
    // Apply the grid state only once, when the grid is ready.
    if (isGridReady && !initialStateApplied) {
      applyGridState(gridState);
      setInitialStateApplied(true);
    }
  }, [applyGridState, gridState, initialStateApplied, isGridReady]);

  const handleFilterChanged = useCallback(() => {
    const { filter } = getState() ?? {};
    onActiveFilterCountChange?.(size(filter?.filterModel));
  }, [getState, onActiveFilterCountChange]);

  useEffect(() => {
    isGridReady && handleFilterChanged();
  }, [handleFilterChanged, isGridReady, rowData]);

  const defaultColDefOverride = useMemo(() => {
    return getDefaultColDefOverride({ reverseNullSort });
  }, [reverseNullSort]);

  return (
    <StyledClientDataGrid
      context={gridContext}
      sideBar={sideBarDef}
      columnDefs={columnDefs}
      loading={!rowData}
      rowData={rowData}
      initialState={gridState}
      groupDisplayType="groupRows"
      groupDefaultExpanded={1}
      getRowId={getRowId}
      getMainMenuItems={getMainMenuItems}
      suppressExcelExport={!hasExportTrait}
      suppressCsvExport={!hasExportTrait}
      defaultColDefOverride={defaultColDefOverride}
      groupRowRendererParams={groupRowRendererParams}
      onStateUpdated={handleStateUpdated}
      onFilterChanged={handleFilterChanged}
      onGridReady={onGridReady}
    />
  );
};
